// Отслеживание реального состояния сети (не только navigator.onLine).
//
// navigator.onLine и события online/offline на Android/TWA отслеживают
// сетевой интерфейс, а не связь, и после возврата сети событие часто не
// приходит — флаг «залипает» в офлайне: баннер «Нет подключения» висел
// вечно, а flush() офлайн-очереди не отправлял изменения до перезагрузки.
//
// Теперь состояние «онлайн» подтверждается реальной пробой GET /api/ping:
// пока итоговое состояние — офлайн, крутится цикл проб (3с → 10с → 30с);
// любой HTTP-ответ = связь есть → уведомляем подписчиков (OfflineIndicator
// снимает баннер, offlineQueue запускает flush). Политика решений — pure
// shared/services/connectivity.ts (тесты там же).

import {
  effectiveOnline,
  shouldKeepProbing,
  nextProbeDelayMs,
  PROBE_TIMEOUT_MS,
} from '@shared/services/connectivity';

type Listener = (online: boolean) => void;

const listeners = new Set<Listener>();
let initialized = false;
let probeOnline: boolean | null = null; // null — проба ещё не выполнялась
let failedProbes = 0;
let probeTimer: ReturnType<typeof setTimeout> | null = null;
let probing = false;

function navFlag(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine;
}

function current(): boolean {
  return effectiveOnline(navFlag(), probeOnline);
}

function notify() {
  const online = current();
  listeners.forEach((fn) => {
    try { fn(online); } catch { /* listener crash isolated */ }
  });
}

async function probe(): Promise<void> {
  if (probing) return;
  probing = true;
  const was = current();
  try {
    // Уникальный параметр — чтобы не совпасть с кешем SW (NetworkFirst для
    // /api/*): офлайн-фоллбэк из кеша дал бы ложный «онлайн».
    await fetch(`/api/ping?t=${Date.now()}`, {
      cache: 'no-store',
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    // Долетели до сервера (любой статус) — связь есть.
    probeOnline = true;
    failedProbes = 0;
  } catch {
    probeOnline = false;
    failedProbes += 1;
  } finally {
    probing = false;
  }
  if (current() !== was) notify();
  scheduleProbeIfNeeded();
}

function scheduleProbeIfNeeded() {
  if (probeTimer) { clearTimeout(probeTimer); probeTimer = null; }
  if (!shouldKeepProbing(navFlag(), probeOnline)) return;
  probeTimer = setTimeout(() => { probeTimer = null; probe(); }, nextProbeDelayMs(failedProbes));
}

function init() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  window.addEventListener('online', () => {
    // Событие пришло — подтверждаем пробой немедленно (флагу в сторону
    // «онлайн» не верим на слово, но и лишние 3с ждать незачем).
    failedProbes = 0;
    probe();
    notify();
  });
  window.addEventListener('offline', () => {
    probeOnline = false;
    notify();
    scheduleProbeIfNeeded();
  });
  // Возврат в приложение — удобный момент перепроверить связь.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && !current()) {
      failedProbes = 0;
      probe();
    }
  });

  // Старт с залипшим флагом «офлайн» (частый кейс TWA) — сразу пробуем.
  if (!current()) probe();
}

export function isOnline(): boolean {
  init();
  return current();
}

/**
 * Сообщить о сетевой ошибке из чужого fetch'а (например, flush поймал
 * TypeError при «онлайн»-флаге — значит, флаг залип в онлайне). Переводит
 * состояние в офлайн и запускает цикл проб, который вернёт онлайн сам.
 */
export function reportConnectivityFailure(): void {
  init();
  const was = current();
  probeOnline = false;
  if (current() !== was) notify();
  scheduleProbeIfNeeded();
}

export function onNetworkChange(fn: Listener): () => void {
  init();
  listeners.add(fn);
  return () => listeners.delete(fn);
}
